import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import General, { Profile } from "./components/General";
import Information from "./components/Information";
import { fetchProfile, updateProfile } from "services/apiService";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SubscriptionCard from "views/dashboard/subscription/components/SubscriptionForm";
import SubscriptionTable from "views/dashboard/subscription/components/SubscriptionTable";
import InviteTeamForm from "views/dashboard/invite/components/InviteTeamForm";
import PricingCard from "views/dashboard/pricing/components/PricingCard";

type Tab = "profile" | "subscription" | "team" | "pricing";

const tabs: { key: Tab; label: string; adminOnly?: boolean }[] = [
  { key: "profile", label: "Profile" },
  { key: "subscription", label: "Subscription", adminOnly: true },
  { key: "team", label: "Team", adminOnly: true },
  { key: "pricing", label: "Pricing", adminOnly: true },
];

const isAdmin = localStorage.getItem("isOrganizationAdmin") === "true";

const ProfileDashboard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<Tab>(
    (searchParams.get("tab") as Tab) || "profile"
  );
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Sync tab state whenever the URL ?tab= param changes
  useEffect(() => {
    const tab = (searchParams.get("tab") as Tab) || "profile";
    setActiveTab(tab);
  }, [searchParams]);

  const handleTabChange = (tab: Tab) => {
    setSearchParams(tab === "profile" ? {} : { tab });
  };

  useEffect(() => {
    const getProfileData = async () => {
      setLoading(true);
      try {
        const profileData = await fetchProfile();
        setProfile(profileData);
      } catch (error) {
        console.error("Failed to fetch profile data", error);
      } finally {
        setLoading(false);
      }
    };
    getProfileData();
  }, []);

  const updateProfileData = async (updatedProfile: Profile) => {
    try {
      await updateProfile(updatedProfile);
      setProfile(updatedProfile);
      toast.success("Profile updated successfully!");
    } catch (error) {
      toast.error("Error updating profile.");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40 text-gray-500 dark:text-gray-400">
        Loading...
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center h-40 text-gray-500 dark:text-gray-400">
        No profile data available.
      </div>
    );
  }

  const visibleTabs = tabs.filter(t => !t.adminOnly || isAdmin);

  return (
    <div className="flex w-full flex-col gap-5">

      {/* ── Tab rail (admins) or page heading (invited users) ──────── */}
      {visibleTabs.length > 1 ? (
        <div className="inline-flex self-start bg-[#6B7280] rounded-xl p-1 gap-1">
          {visibleTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`px-5 py-2 text-sm font-semibold rounded-lg transition-all duration-150 focus:outline-none whitespace-nowrap ${
                activeTab === tab.key
                  ? "bg-white text-[#1F2937] shadow-sm"
                  : "text-white/60 hover:text-white hover:bg-white/10"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      ) : (
        <div>
          <h1 className="text-xl font-semibold text-[#1F2937]">My Profile</h1>
          <p className="text-xs text-[#6B7280] mt-1">Manage your account and dashboard access.</p>
        </div>
      )}

      {/* ── Tab content panel ──────────────────────────────────────── */}
      <div className="bg-white border border-[#E4E7EB] rounded-xl shadow-sm p-6">

        {/* Profile tab */}
        {activeTab === "profile" && (
          <div className="flex flex-col gap-5">
            <Information profile={profile} />
            <General profile={profile} updateProfileData={updateProfileData} />
          </div>
        )}

        {/* Subscription tab */}
        {activeTab === "subscription" && (
          <div className="grid h-full grid-cols-1 gap-5 md:grid-cols-2">
            <SubscriptionCard subscriptionPlan={profile.organization?.subscription_plan} />
            <SubscriptionTable />
          </div>
        )}

        {/* Team tab */}
        {activeTab === "team" && (
          <div className="grid grid-cols-12 gap-5">
            <div className="col-span-12 md:col-span-6">
              <InviteTeamForm />
            </div>
          </div>
        )}

        {/* Pricing tab */}
        {activeTab === "pricing" && (
          <div className="w-full">
            <PricingCard />
          </div>
        )}

      </div>

      <ToastContainer />
    </div>
  );
};

export default ProfileDashboard;
